import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "components/app/dialog/dialog";
import { Tabs, TabsContent } from "components/ui/tabs";
import ImageUrlProfile from "components/app/image-url/ImageUrlProfile";
import { ScrollArea } from "components/ui/scroll-area";
import { Skeleton } from "components/ui/skeleton";
import { ArrowLeft, Loader, LoaderCircle, UserPlus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { AppLog } from "services";
import { useAppSelector } from "store/store";
import { RequestApi, RouteApi, StatusMsg } from "utils";
import RadioGroupWithLabel from "components/app/function/RadioGroupWithLabel";
import InputHideShow from "components/app/function/InputHideShow";
import DialogAddUserPermission from "./DialogAddUserPermission";
import { AppRecord, AppRecordStatus } from "models/appRecord";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation } from "react-router-dom";

interface DialogProps {
  user: any;
}

export default function DialogUserPermissions({ user }: DialogProps) {
  const { token } = useAppSelector((state) => state.auth);
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [userPermissions, setUserPermissions] = useState<any>();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [loadingPermission, setLoadingPermission] = useState<boolean>(false);
  const [loadingRemovePermission, setLoadingRemovePermission] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState("noTap");
  const [userClicked, setUserClicked] = useState<any>();
  const [searchUserInput, setSearchUserInput] = useState<string>("");
  const [isDialogAddUserOpen, setIsDialogAddUserOpen] = useState<boolean>(false);
  const [filteredUsers, setFilteredUsers] = useState<any>();
  const [userLoading, setUserLoading] = useState<any>(null);
  const [userFilterLoading, setUserFilterLoading] = useState<any>(null);
  const [formDataUserPermission, setFormDataUserPermission] = useState<any>({
    role_permission: "",
    password: "",
  });
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");

  const [validateText, setValidateText] = useState<string>("");

  const accountState = {
    currentPage: 1,
    currentIndex: 0,
    nextPageUrl: "",
    totalRecord: 0,
    loadingStatus: AppRecordStatus.loading,
  };

  const fetchGetUser = async () => {
    try {
      setLoadingPage(true);
      let url: string = "";
      url = RouteApi.getShopPermission;
      url += `token=${token}`;
      const data = {
        shop_id: user.shop_id,
      };
      const response = await RequestApi(url, { data });

      if (response.status === "success") {
        const stateRecord: AppRecord = {
          currentPage: 1,
          currentIndex: 0,
          nextPageUrl: response["user_permissions"]["next_page_url"] ?? "",
          totalRecord: 0,
          loadingStatus: response["user_permissions"]["next_page_url"] != null ? AppRecordStatus.loaded : AppRecordStatus.noLoadMore,
        };
        setUserPermissions(response["user_permissions"]);
        setUserLoading(stateRecord);
      } else {
        AppLog.show({ message: response.msg, status: StatusMsg.getType(response.status) });
      }
    } catch (error) {
      AppLog.show({
        message: `[fetchGetUser]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setLoadingPage(false);
    }
  };

  const fetchPermissionSearchUser = async () => {
    try {
      setLoadingPage(true);
      let url: string = "";
      url = RouteApi.getShopPermissionSearchUser;
      url += `token=${token}`;
      const data = {
        shop_id: user.shop_id,
        search_text: searchUserInput,
      };

      const response = await RequestApi(url, { data });

      if (response.status === "success") {
        const stateRecord: AppRecord = {
          currentPage: 1,
          currentIndex: 0,
          nextPageUrl: response["users"]["next_page_url"] ?? "",
          totalRecord: 0,
          loadingStatus: response["users"]["next_page_url"] != null ? AppRecordStatus.loaded : AppRecordStatus.noLoadMore,
        };
        setFilteredUsers(response["users"]);
        setUserFilterLoading(stateRecord);
      } else {
        AppLog.show({ message: response.msg, status: StatusMsg.getType(response.status) });
      }
    } catch (error) {
      AppLog.show({
        message: `[fetchPermissionSearchUser]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setLoadingPage(false);
    }
  };

  const fetchGetMoreUser = async ({ next }: { next: string }) => {
    setUserLoading({
      ...accountState,
      loadingStatus: AppRecordStatus.loadingMore,
    });

    await RequestApi(next).then((responses) => {
      if (responses.status === "success") {
        const stateRecord: AppRecord = {
          currentPage: 1,
          currentIndex: 0,
          nextPageUrl: responses["user_permissions"]["next_page_url"] ?? "",
          totalRecord: 0,
          loadingStatus: responses["user_permissions"]["next_page_url"] !== null ? AppRecordStatus.noLoadMore : AppRecordStatus.loaded,
        };

        const tmpPosts = [...userPermissions["data"], ...responses["user_permissions"]["data"]];

        const newPost = { ...userPermissions, data: tmpPosts };
        setUserPermissions(newPost);
        setUserLoading(stateRecord);
      }
    });
  };

  const fetchMorePermissionSearchUser = async ({ next }: { next: string }) => {
    setUserFilterLoading({
      ...accountState,
      loadingStatus: AppRecordStatus.loadingMore,
    });

    await RequestApi(next).then((responses) => {
      if (responses.status === "success") {
        const stateRecord: AppRecord = {
          currentPage: 1,
          currentIndex: 0,
          nextPageUrl: responses["users"]["next_page_url"] ?? "",
          totalRecord: 0,
          loadingStatus: responses["users"]["next_page_url"] !== null ? AppRecordStatus.noLoadMore : AppRecordStatus.loaded,
        };

        const tmpPosts = [...filteredUsers["data"], ...responses["users"]["data"]];

        const newPost = { ...filteredUsers, data: tmpPosts };
        setFilteredUsers(newPost);
        setUserFilterLoading(stateRecord);
      }
    });
  };

  const handleSubmitForm = (type: string) => {
    if (type === "update") {
      if (formDataUserPermission.password !== "") {
        onSubmitUpdate();
      } else {
        setValidateText("Password is required*");
      }
    } else if (type === "remove") {
      if (formDataUserPermission.password !== "") {
        onSubmitRemove();
      } else {
        setValidateText("Password is required*");
      }
    } else if (type === "invite") {
      if (formDataUserPermission.password !== "") {
        onSubmitSendInvite();
      } else {
        setValidateText("Password is required*");
      }
    }
  };

  const onSubmitSendInvite = async () => {
    setLoadingPermission(true);
    try {
      const url = `${RouteApi.submitInsertShopPermission}token=${token}`;
      const data = {
        shop_id: id,
        user_id: userClicked.id,
        permission: formDataUserPermission.role_permission,
        password: formDataUserPermission.password,
      };

      const response = await RequestApi(url, { data });

      if (response.status === "success") {
        const resUser = response.user_page_permission;
        // setUserPermissions((prev: any) => ({
        //   ...prev,
        //   data: Array.isArray(resUser)
        //     ? [...prev.data, ...resUser]
        //     : [...prev.data, resUser],
        // }));
        setActiveTab("noTap");
        setUserClicked(null);
        setValidateText("");
      } else {
        setValidateText(response.msg);
      }
    } catch (error) {
      AppLog.show({
        message: `[onSubmitSendInvite]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setLoadingPermission(false);
    }
  };

  const onSubmitRemove = async () => {
    setLoadingRemovePermission(true);
    try {
      const url = `${RouteApi.submitDeleteShopPermission}token=${token}`;
      const data = {
        shop_id: id,
        id: userClicked.id,
        password: formDataUserPermission.password,
      };
      const updateUser = userPermissions.data.find((element: any) => element.id === userClicked.id);

      const response = await RequestApi(url, { data });

      if (response.status === "success") {
        setActiveTab("noTap");
        setUserClicked(null);
        setValidateText("");
        setUserPermissions((prev: any) => ({
          ...prev,
          data: prev.data.filter((element: any) => element.id !== updateUser.id),
        }));
      }
    } catch (error) {
      AppLog.show({
        message: `[onSubmitRemove]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setLoadingRemovePermission(false);
    }
  };

  const onSubmitUpdate = async () => {
    setLoadingPermission(true);
    try {
      const url = `${RouteApi.submitUpdateShopPermission}token=${token}`;
      const data = {
        shop_id: id,
        id: userClicked.id,
        permission: formDataUserPermission.role_permission,
        password: formDataUserPermission.password,
      };

      const response = await RequestApi(url, { data });

      if (response.status === "success") {
        const resUser = response.user_page_permission;

        setUserPermissions((prev: any) => ({
          ...prev,
          data: prev.data.map((item: any) => (item.id === resUser.id ? { ...item, ...resUser } : item)),
        }));
        setActiveTab("noTap");
        setUserClicked(null);
        setValidateText("");
      }
    } catch (error) {
      AppLog.show({
        message: `[onSubmitUpdate]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setLoadingPermission(false);
    }
  };

  useEffect(() => {
    if (activeTab === "noTap") {
      setFormDataUserPermission({
        role_permission: userClicked,
        password: "",
      });
    } else {
      setFormDataUserPermission({
        role_permission: userClicked?.permission || "",
        password: "",
      });
    }
  }, [activeTab]);

  useEffect(() => {
    if (isDialogAddUserOpen) {
      if (searchUserInput) {
        fetchPermissionSearchUser();
      }
    }
  }, [searchUserInput, isDialogAddUserOpen]);

  useEffect(() => {
    if (isDialogOpen) {
      fetchGetUser();
    }
  }, [isDialogOpen]);

  return (
    <Dialog open={isDialogOpen}>
      <button
        onClick={() => {
          setIsDialogOpen(true);
        }}
        className="rounded-lg p-2 w-full bg-gray-500 hover:bg-gray-400 flex items-center justify-center"
      >
        <span className="font-semibold text-xs text-white">User Permissions</span>
      </button>

      <DialogContent isShowBtnXClose={false} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>User Permissions</DialogTitle>
          <DialogDescription>Manage and assign permissions for users. Select roles and access levels to control their actions within the system.</DialogDescription>
          <div
            className="absolute flex items-center justify-center right-4 top-1 z-40 cursor-pointer rounded-full bg-gray-200 dark:bg-accent w-[28px] h-[28px]"
            onClick={() => {
              setIsDialogOpen(false);
              setFormDataUserPermission(null);
              setValidateText("");
            }}
          >
            <X />
          </div>
        </DialogHeader>
        <div>
          {loadingPage ? (
            [...Array(4)].map((_, index) => {
              return (
                <div key={index} onClick={() => console.log("Click")} className="p-4 flex justify-between items-center mb-2">
                  <div>
                    <div className="flex items-center">
                      <Skeleton className="w-[70px] h-[70px] border rounded-full" />
                      <div className="ml-4 space-y-2">
                        <Skeleton className="w-[200px] h-[20px]" />
                        <Skeleton className="w-[200px] h-[20px]" />
                      </div>
                    </div>
                  </div>
                  <Skeleton className="w-[50px] h-[20px]" />
                </div>
              );
            })
          ) : (
            <>
              {/* Tabs */}
              <Tabs defaultValue="noTap" value={activeTab} onValueChange={setActiveTab} className="w-[400px]">
                <ScrollArea className="h-[500px]">
                  <TabsContent value="noTap">
                    <div className="flex justify-start items-center w-full mb-2">
                      <button onClick={() => setIsDialogAddUserOpen(true)} className="flex items-center space-x-1">
                        <UserPlus className="text-yellow-500" />
                        <p className="font-semibold">Add user</p>
                      </button>
                    </div>

                    {userPermissions ? (
                      <div id="scrollableDiv1" className="max-h-[500px] overflow-y-auto custom-scrollbar">
                        <InfiniteScroll
                          className={`relative max-h-[500px]`}
                          dataLength={userPermissions["data"]?.length}
                          next={() =>
                            fetchGetMoreUser({
                              next: userLoading.nextPageUrl,
                            })
                          }
                          scrollThreshold="90%"
                          hasMore={!!userLoading.nextPageUrl}
                          scrollableTarget="scrollableDiv1"
                          loader={
                            <div className="absolute bottom-2 left-0 w-full flex justify-center">
                              <LoaderCircle size={30} className="animate-spin text-yellow-400" />
                            </div>
                          }
                          endMessage={
                            <p className="absolute bottom-0 left-0 w-full text-center">
                              <b>Nothing more to load!</b>
                            </p>
                          }
                        >
                          <div className="grid grid-cols-1 space-y-2 mb-20">
                            {userPermissions?.data?.map((item: any) => (
                              <div
                                key={item.id}
                                onClick={() => {
                                  setActiveTab("tap");
                                  setUserClicked(item);
                                }}
                                className="p-4 cursor-pointer flex justify-between items-center hover:bg-slate-100 dark:hover:bg-accent rounded-lg"
                              >
                                <div className="flex items-center">
                                  <ImageUrlProfile isLinkProfile={false} user={item?.permission_user} className="w-[50px] h-[50px] border rounded-full" />
                                  <div className="ml-2">
                                    <h4 className="font-medium line-clamp-1">{item?.permission_user?.name}</h4>
                                    <h5 className="text-muted-foreground text-xs line-clamp-1">{item?.permission_user?.bio}</h5>
                                  </div>
                                </div>

                                <p className="text-yellow-500 text-sm">{item?.permission}</p>
                              </div>
                            ))}
                          </div>
                        </InfiniteScroll>
                      </div>
                    ) : (
                      <></>
                    )}
                  </TabsContent>

                  {/* User Detail View */}
                  <TabsContent value="tap">
                    <button
                      onClick={() => {
                        setActiveTab("noTap");
                        setUserClicked(null);
                        setValidateText("");
                      }}
                    >
                      <ArrowLeft />
                    </button>
                    <div className="w-full space-y-4 mt-2">
                      <div className="flex items-center">
                        <ImageUrlProfile isLinkProfile={false} user={userClicked?.permission_user || userClicked} className="w-[50px] h-[50px] border rounded-full" />
                        <div className="ml-2">
                          <h4 className="font-medium line-clamp-1">{userClicked?.permission_user?.name || userClicked?.name}</h4>
                          <h5 className="text-muted-foreground text-xs line-clamp-1">{userClicked?.permission_user?.bio || userClicked?.bio}</h5>
                        </div>
                      </div>

                      <div>
                        <h5 className="font-medium">Permission Role</h5>
                        <RadioGroupWithLabel
                          value={userClicked?.permission}
                          className="flex"
                          items={[
                            { id: "admin", label: "Admin", value: "Admin" },
                            { id: "editor", label: "Editor", value: "Editor" },
                          ]}
                          onChange={(value) =>
                            setFormDataUserPermission((prev: any) => ({
                              ...prev,
                              role_permission: value, // Correctly updating user_permissions
                            }))
                          }
                        />
                      </div>

                      <div>
                        <h5 className="font-medium">Password</h5>
                        <p className="text-xs text-muted-foreground">To update or remove user, please input your current password.</p>
                        <InputHideShow
                          onChange={(event) =>
                            setFormDataUserPermission((prev: any) => ({
                              ...prev,
                              password: event.target.value,
                            }))
                          }
                          className="w-full"
                        />
                        <p className="text-xs font-semibold text-destructive">{validateText}</p>
                      </div>

                      {userClicked?.user_shop_permission === null ? (
                        <>
                          <button
                            onClick={() => {
                              handleSubmitForm("invite");
                            }}
                            className="w-full flex justify-center text-white bg-yellow-400 py-2 rounded-lg font-semibold text-sm"
                          >
                            {loadingPermission ? <Loader className="animate-spin" /> : "Send Invite"}
                          </button>
                        </>
                      ) : (
                        <div className="space-y-2">
                          <button onClick={() => handleSubmitForm("update")} className="w-full flex justify-center text-white bg-yellow-400 py-2 rounded-lg font-semibold text-sm">
                            {loadingPermission ? <Loader className="animate-spin" /> : "Update"}
                          </button>

                          <button onClick={() => handleSubmitForm("remove")} className="w-full flex justify-center text-white bg-destructive py-2 rounded-lg font-semibold text-sm">
                            {loadingRemovePermission ? <Loader className="animate-spin" /> : "Remove"}
                          </button>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            </>
          )}
        </div>
        <DialogFooter className="sm:justify-end">
          {/* <AlertDialogCom
          alertTitle="Are you sure?"
          alertDescription="This action cannot be undone. Are you sure you want to proceed?"
          handleSubmit={() => ""}
          nameSubmitButton="Confirm"
          loading={loadingUpdatePageStatus}
        >
          <Button className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-2 rounded-xl shadow-md hover:scale-105 hover:shadow-lg transition-all duration-300">
            {loadingUpdatePageStatus ? (
              <Loader className="animate-spin w-5 h-5" />
            ) : (
              "Continue"
            )}
          </Button>
        </AlertDialogCom> */}
        </DialogFooter>
      </DialogContent>

      <DialogAddUserPermission
        accountLoading={userFilterLoading}
        fetchMorePermissionSearchUser={fetchMorePermissionSearchUser}
        filteredUsers={filteredUsers}
        isDialogAddUserOpen={isDialogAddUserOpen}
        setActiveTab={setActiveTab}
        setIsDialogAddUserOpen={setIsDialogAddUserOpen}
        setSearchUserInput={setSearchUserInput}
        setUserClicked={setUserClicked}
      />
    </Dialog>
  );
}
